import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getValidationShema,
} from "./constants/validationSchema";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { PLACEHOLDER_MESSAGES } from "./constants";
import "./styles/Step2Form.scss";
import { useStateMachine } from "little-state-machine";
import { updateAction } from "scenes/ProjectMultiStepForm/store";
import TooltipField from "scenes/ProjectMultiStepForm/components/TooltipField";
import { AUTHORIZATION_IMAGES_VALUES, AUTHORIZATION_PROJECT_VALUES } from "./constants/select-values";
import CustomSelect from "utils/components/CustomSelect";
import CustomFileInput from "./components/CustomFileInput";
import { graphql } from "react-apollo";
import { updateProjectInformation } from "../../../Step1/components/Step1Form/mutation";
import proquinalHistory from "services/browser-history";

const Step3Form = ({ updateProjectInformation }) => {
  const intl = useIntl();
  const { state, actions } = useStateMachine({ updateAction });

  const updateField = (setFieldValue) => (e) => {
    const { name, value } = e.target;
    setFieldValue(name, value);
  }

  const initialValues = {
    authorizationImages: state?.authorizationImages || "",
    authorizationProject: state?.authorizationProject || "",
    authorizationSupport: state?.authorizationSupport || "",
    giveCredits: state?.giveCredits || "",
    credits: state?.credits || "",
    bussinessUnitPreviewAuthorization: state?.bussinessUnitPreviewAuthorization || ""
  }
  
  const handleSubmit = (values) => {
    const {
      authorizationImages: authorizationImagesValue,
      authorizationProject,
      authorizationSupport,
      credits,
      bussinessUnitPreviewAuthorization
    } = values;

    const input = {
      authorization: authorizationImagesValue,
      authorizationByClient: authorizationProject ? "Yes" : "No",
      authorizationFile: authorizationSupport,
      creditsForImages: credits || "",
      declarationOfUse: bussinessUnitPreviewAuthorization
    };

    const filteredInput = Object.fromEntries(
      Object.entries(input).filter(([key, value]) => 
        key === "declarationOfUse" || key === "authorizationByClient" || value
    )
    );

    return updateProjectInformation({
      variables: {
        projectId: state.projectId,
        ...filteredInput
      },
    })
      .then((response) => {
        const { updateProjectData } = response.data;

        if (updateProjectData?.project) {
          actions.updateAction({
            ...state,
            ...values,
            projectId: updateProjectData?.project.id,
          });

          proquinalHistory.push({
            pathname: "/project/creation-form/step4"
          });
        }
      })
      .catch((err) => {
        const { graphQLErrors, netWorkError } = err;

        if (graphQLErrors || netWorkError) {
          console.error("Error update Project GraphQL:", graphQLErrors);
          console.error("Error update Project Network:", netWorkError);
        }
      });
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={getValidationShema(intl)}
      enableReinitialize
    >
      {({ 
        setFieldValue, 
        values: {
          authorizationImages,
          authorizationProject,
          authorizationSupport,
          giveCredits,
          credits,
          bussinessUnitPreviewAuthorization
        }, isSubmitting }) => (
        <Form id="step-0-form" onKeyDown={e => e && e.key === "Enter" && e.preventDefault()}>
          <div className="step-0-form__container d-flex flex-column">
            <div className="step-0-form__selects d-lg-flex">
              <div className="col-12 col-lg-12 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label d-inline p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="authorizationImages"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step3.field.authorizationImages"
                    defaultMessage="Autorización de uso de las imágenes"
                  />
                  {"*"}
                </label>
                <TooltipField tooltipMessage={(
                  <FormattedMessage 
                    id="ProjectStepForm.step3.field.tooltip.authorizationImages"
                    defaultMessage="Elije la autorización de uso de la lista desplegable. Si eliges uso restringido, es necesario solicitar permiso al área de mercadeo de la unidad de negocio."
                  />
                )} />
                <CustomSelect
                  config={{
                    name: "authorizationImages",
                    initialValue: authorizationImages && AUTHORIZATION_IMAGES_VALUES.find(({ value }) => value === authorizationImages).label,
                    placeholderMessage: intl.formatMessage(
                      PLACEHOLDER_MESSAGES.authorizationImages
                    )
                  }}
                  updateField={setFieldValue}
                  returnValue="id"
                  selectValues={AUTHORIZATION_IMAGES_VALUES}
                />
              </div>
            </div>
            <div className="step-0-form__selects d-lg-flex">
              <div className="col-12 col-lg-12 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="authorizationProject"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step3.field.authorizationProject"
                    defaultMessage="Proyecto autorizado por el cliente"
                  />
                  {"*"}
                </label>
                <CustomSelect
                  config={{
                    name: "authorizationProject",
                    initialValue: authorizationProject && AUTHORIZATION_PROJECT_VALUES.find(({ value }) => value === authorizationProject).label,
                    placeholderMessage: intl.formatMessage(
                      PLACEHOLDER_MESSAGES.authorizationProject
                    )
                  }}
                  updateField={setFieldValue}
                  selectValues={AUTHORIZATION_PROJECT_VALUES}
                  returnValue="id"
                />
              </div>
            </div>
            {(state.authorizationByClient || authorizationProject === "Yes") && 
                <CustomFileInput />}
            <div className="step-0-form__selects d-lg-flex">
              <div className="col-12 col-lg-12 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="giveCredits"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step3.field.giveCredits"
                    defaultMessage="¿Al usar las imágenes o videos del proyecto debemos dar créditos?"
                  />
                  {"*"}
                </label>
                <CustomSelect
                  config={{
                    name: "giveCredits",
                    initialValue: giveCredits && AUTHORIZATION_PROJECT_VALUES.find(({ value }) => value === giveCredits).label,
                    placeholderMessage: intl.formatMessage(
                      PLACEHOLDER_MESSAGES.giveCredits
                    )
                  }}
                  updateField={setFieldValue}
                  selectValues={AUTHORIZATION_PROJECT_VALUES}
                  returnValue="id"
                />
              </div>
            </div>
            {giveCredits && giveCredits === "Yes" && <div className="step-0-form__selects d-lg-flex">
              <div className="col-12 col-lg-12 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold d-inline"
                  htmlFor="credits"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step3.field.credits"
                    defaultMessage="¿A quién o quiénes se deben mencionar en los créditos? Ej: Empresa, Fotógrafo, Agencia."
                  />
                </label>
                <TooltipField tooltipMessage={(
                  <FormattedMessage 
                    id="ProjectStepForm.step3.field.tooltip.credits"
                    defaultMessage="Indica el texto que debe acompañar la foto para indicar el fotógrafo o titular de los derechos."
                  />
                )} />
                <Field
                  id="credits"
                  name="credits"
                  className="form-control bg-transparent form-control-placeholder"
                  type="text"
                  value={credits}
                  placeholder={intl.formatMessage(PLACEHOLDER_MESSAGES.credits)}
                  onChange={updateField(setFieldValue)}
                />
                <ErrorMessage
                  className="form-error-message"
                  component="div"
                  name="credits"
                />
              </div>
            </div>}
            <div className="step-0-form__selects d-lg-flex">
              <div className="col-12 col-lg-12 px-0 mb-3 mb-lg-0">
                <label
                  className="step-0-form__label p-0 m-0 mb-lg-1 font-weight-bold"
                  htmlFor="bussinessUnitPreviewAuthorization"
                >
                  <FormattedMessage
                    id="ProjectStepForm.step3.field.bussinessUnitPreviewAuthorization"
                    defaultMessage="Declaración previa de autorización de la Unidad de Negocio"
                  />
                </label>
                <CustomSelect
                  config={{
                    name: "bussinessUnitPreviewAuthorization",
                    initialValue: bussinessUnitPreviewAuthorization && AUTHORIZATION_PROJECT_VALUES.find(({ value }) => value === bussinessUnitPreviewAuthorization).label,
                    placeholderMessage: intl.formatMessage(
                      PLACEHOLDER_MESSAGES.bussinessUnitPreviewAuthorization
                    )
                  }}
                  updateField={setFieldValue}
                  selectValues={AUTHORIZATION_PROJECT_VALUES}
                  returnValue="id"
                />
                <ErrorMessage
                  className="form-error-message"
                  component="div"
                  name="bussinessUnitPreviewAuthorization"
                />
              </div>
            </div>
            <button
              type="submit"
              className="step-0-form__submit-btn px-5 py-2 mt-4"
              disabled={isSubmitting}
            >
              <FormattedMessage
                id="ProjectStepForm.button.continue"
                defaultMessage="Continuar"
              />
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default graphql(updateProjectInformation, { name: "updateProjectInformation" }) (Step3Form);
